import { PAGE_HEROES } from "../content/page-copy";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import { LiveHonestyGauge } from "../components/HonestyGauge";

/**
 * HonestyPage — the live honesty ratio, public. (Honest marketing.)
 *
 * TrustPage promises "a live honesty rate … the aggregate trust basis,
 * queryable — by you." This page is where that line lands: the actual number,
 * fetched live from the federation's public honesty endpoint, shown as-is. No
 * spin, no rounding up, and explicitly honest about its own freshness — if the
 * number is stale because the federation hasn't redeployed, the page says so.
 * Showing a low number, truthfully, is the entire point: here's the real world.
 */
const HERO = PAGE_HEROES['honesty']!;

export function HonestyPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      {/* Hero */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              Most companies show you the metrics that flatter them. This one we
              can't tune for a slide — it's computed by the system, about the
              system, and published whether it's good news or not. It's the
              fraction of the platform's own change record that has passed QC.
              Right now it's climbing. We're showing it to you anyway.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* The live gauge */}
      <Section tint="sunk">
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <LiveHonestyGauge />
          </motion.div>
        </Container>
      </Section>

      {/* What the number means */}
      <Section>
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">What you're looking at</p>
            <h2 className="text-display-md text-balance">
              The share of the platform's own record that has passed QC.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-6">
              Every deliberate change to ComOS is tracked as a unit in an
              evidence chain. A unit counts toward the ratio only once it has
              actually passed the verification gate — not when it's asserted,
              not when it's merged, only when it's proven. The number is the
              fraction passed across the whole chain, read live from the same
              federation tool the internal instrument panel reads. That's why
              there's no marketing lever on it: it's computed by the system,
              about the system.
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* Why we'd publish a low number */}
      <Section tint="sunk">
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">Why show it at all</p>
            <h2 className="text-display-md text-balance">
              A number you can only trust if we'd show it when it's low.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-6">
              The entire value of an honesty metric is destroyed the moment
              you'd hide it for being unflattering. So we don't. The ratio is a
              rate, like uptime — generated continuously by a mechanism,
              climbing as the record gets proven, and visible the whole way up.
              You're watching the real climb, in public, not a finished number
              staged for launch.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/trust"
                className={buttonStyles({ variant: "primary" })}
              >
                How the mechanism works
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
